import { createRouter, createWebHistory } from 'vue-router'
import HomeView from '../views/HomeView.vue'

const routes = [
  {
    path: '/',
    name: 'home',
    component: HomeView,
    meta: {
      title: 'Safari Strives | Empowering Economic Independence in Rwanda',
      description: 'Safari Strives builds economic independence through a self-funding ecosystem in Rwanda. Zero-interest microloans, job creation, and sustainable development for women entrepreneurs.',
      keywords: 'Safari Strives, women empowerment Rwanda, social enterprise Africa, microloans, TechStyle hub, Safari Chicken Initiative, impact stories',
      ogImage: '/icons/logoicononly.jpg'
    }
  },
  {
    path: '/about',
    name: 'about',
    component: () => import(/* webpackChunkName: "about" */ '../views/AboutView.vue'),
    meta: {
      title: 'About Safari Strives | Our Mission & Vision',
      description: 'Learn about Safari Strives mission to create economic independence through sustainable social enterprises in Rwanda. Founded by Elia Imani with support from Church of the Brethren.',
      keywords: 'Safari Strives mission, Elie Imani, Our Roots, Financial Stewardship, Rwanda women entrepreneurs, Church of the Brethren Rwanda',
      ogImage: '/icons/logoicononly.jpg'
    }
  },
  {
    path: '/impact',
    name: 'impact',
    component: () => import(/* webpackChunkName: "impact" */ '../views/ImpactView.vue'),
    meta: {
      title: 'Our Impact | Safari Strives Success Stories',
      description: 'See the real impact of Safari Strives programs. Watch impact films, view financial snapshots, and learn how we are transforming lives in Rwanda through sustainable economic development.',
      keywords: 'Safari Strives impact, success stories, 2024 impact film, 2025 impact film, financial snapshot, families reached in Rwanda',
      ogImage: '/icons/logoicononly.jpg'
    }
  },
  {
    path: '/team',
    name: 'team',
    component: () => import(/* webpackChunkName: "team" */ '../views/TeamView.vue'),
    meta: {
      title: 'Our Team | Safari Strives Leadership',
      description: 'Meet the Safari Strives team leading economic transformation in Rwanda. Our dedicated leadership, partners, and advisors driving women-led initiatives.',
      keywords: 'Safari Strives team, leadership, Rwanda operations, partners, advisors, women led initiatives',
      ogImage: '/icons/logoicononly.jpg'
    }
  },
  {
    path: '/contact',
    name: 'contact',
    component: () => import(/* webpackChunkName: "contact" */ '../views/ContactView.vue'),
    meta: {
      title: 'Contact Us | Safari Strives',
      description: 'Get in touch with Safari Strives. Volunteer opportunities, partnership inquiries, and donation information. Located in Kigali, Rwanda.',
      keywords: 'Contact Safari Strives, volunteer, partnership inquiries, donations, Kigali Rwanda nonprofit contact',
      ogImage: '/icons/logoicononly.jpg'
    }
  },
  {
    path: '/programs/microloan',
    name: 'microloan-program',
    component: () => import(/* webpackChunkName: "microloan-program" */ '../views/MicroloanProgramView.vue'),
    meta: {
      title: 'Microloan Program | Zero-Interest Loans in Rwanda',
      description: 'Safari Strives offers zero-interest, in-kind microloans delivered within 24 hours. Empowering women entrepreneurs in Rwanda with fast, dignified capital access.',
      keywords: 'Safari Strives microloan program, zero interest loans Rwanda, in-kind capital, women entrepreneurs, community lending',
      ogImage: '/icons/logoicononly.jpg'
    }
  },
  {
    path: '/donate',
    name: 'donate',
    component: () => import(/* webpackChunkName: "donate" */ '../views/DonateView.vue'),
    meta: {
      title: 'Donate | Support Safari Strives Mission',
      description: 'Support women entrepreneurs in Rwanda through zero-interest microloans, job creation, and community development. Your donation creates lasting change with dignity.',
      keywords: 'donate Safari Strives, support women entrepreneurs Rwanda, microloans donation, community development funding',
      ogImage: '/icons/logoicononly.jpg'
    }
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  scrollBehavior(to: any, from: any, savedPosition: any) {
    // If there's a saved position (e.g., browser back/forward), restore it
    if (savedPosition) {
      return savedPosition
    }
    // If route has a hash, scroll to the element
    if (to.hash) {
      return {
        el: to.hash,
        behavior: 'smooth'
      }
    }
    // Otherwise, scroll to top
    return { top: 0, behavior: 'smooth' }
  }
})

const defaultMeta = {
  title: 'Safari Strives | Empowering Economic Independence in Rwanda',
  description: 'Safari Strives builds economic independence through a self-funding ecosystem in Rwanda. Zero-interest microloans, job creation, and sustainable development.',
  keywords: 'Safari Strives, women economic empowerment, Rwanda social enterprise, microloans, TechStyle hub, Safari Chicken Initiative',
  ogImage: '/icons/logoicononly.jpg'
}

const updateMetaTag = (attribute: string, attributeValue: string, content: string) => {
  if (typeof document === 'undefined') return
  let tag = document.querySelector(`meta[${attribute}="${attributeValue}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute(attribute, attributeValue)
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', content)
}

const updateTitle = (title: string) => {
  if (typeof document === 'undefined') return
  document.title = title
}

router.afterEach((to: any) => {
  if (typeof window === 'undefined') return
  const title = to.meta?.title || defaultMeta.title
  const description = to.meta?.description || defaultMeta.description
  const keywords = to.meta?.keywords || defaultMeta.keywords
  const ogImage = to.meta?.ogImage || defaultMeta.ogImage
  const url = window.location.href

  // Update title
  updateTitle(title)

  // Update standard meta tags
  updateMetaTag('name', 'description', description)
  updateMetaTag('name', 'keywords', keywords)

  // Update Open Graph tags
  updateMetaTag('property', 'og:title', title)
  updateMetaTag('property', 'og:description', description)
  updateMetaTag('property', 'og:image', window.location.origin + ogImage)
  updateMetaTag('property', 'og:url', url)
  updateMetaTag('property', 'og:type', 'website')
  updateMetaTag('property', 'og:site_name', 'Safari Strives')

  // Update Twitter Card tags
  updateMetaTag('name', 'twitter:card', 'summary_large_image')
  updateMetaTag('name', 'twitter:title', title)
  updateMetaTag('name', 'twitter:description', description)
  updateMetaTag('name', 'twitter:image', window.location.origin + ogImage)
})

export default router
